import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MailOptOutType } from '__generated__/graphql';

import UI from '../../common/UI';
import UnsubscribeMutation from './UnsubscribeMutation';
import useDocumentTitle from '../../common/useDocumentTitle';
import useMutation from '../../api/useMutation';

export interface UnsubscribePageProps {
  email: string; // Encoded
  token: string;
  type: MailOptOutType;
}

const UnsubscribePage = ({ email, token, type }: UnsubscribePageProps) => {
  const { t } = useTranslation();

  useDocumentTitle([
    t('unsubscribe'),
    'Atleta',
  ]);

  return (
    <UI.Card sc={{ padding: [4, 6], mx: [3, 4] }}>
      <UnsubscribeForm
        email={decodeURIComponent(email)}
        token={token}
        mailType={type}
      />
    </UI.Card>
  );
};

interface UnsubscribeFormProps {
  email: string;
  token: string;
  mailType: MailOptOutType;
}

const UnsubscribeForm = ({ email, token, mailType }: UnsubscribeFormProps) => {
  const { t } = useTranslation();

  const [success, setSuccess] = useState<boolean>(false);

  const types = Object.values(MailOptOutType);

  const getValues = (selectedMailType: MailOptOutType) => ({
    types: types.filter((type) => type === selectedMailType),
  });

  const form = useForm({
    values: getValues(mailType),
  });

  const [unsubscribe, { error, loading }] = useMutation(
    UnsubscribeMutation,
    {
      variables: {
        email,
        token,
        ...form.values,
      },
      onCompleted: ({ unsubscribe: success }) => {
        setSuccess(success);
      },
    },
  );

  return (
    <UI.Form onSubmit={() => { setSuccess(false); unsubscribe(); }}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        {success && (
          <UI.Success>
            {t('you_are_unsubscribed')}
          </UI.Success>
        )}

        <UI.InputGroup>
          <UI.InputLabel htmlFor="EmailAddress">
            {t('email_address')}
          </UI.InputLabel>
          <UI.Input defaultValue={email} disabled id="EmailAddress" />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel>
            {t('mail_opt_out_type')}
          </UI.InputLabel>
          {types.map((type) => (
            <UI.Checkbox {...form.checklist('types', type)} key={type}>
              {t(`mail_opt_out.${type}`)}
            </UI.Checkbox>
          ))}
        </UI.InputGroup>

        <UI.Button
          type="submit"
          sc={{ brand: 'primary', block: true }}
          disabled={loading || form.values.types.length === 0}
        >
          {t('unsubscribe')}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default UnsubscribePage;
